import React, { useState } from 'react';
import { Button, Divider } from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';

import { FALLBACK_AVATAR, resolveCollectorUser } from '../utils/consultationHelpers';
import styles from '../styles.module.scss';
import { CaseType, ICase, Status } from '@brands/services/cases/types/ICase';
import { UserProfile, UserRoleName } from '@brands/services/identity/types/UserProfile';
import { getUserAvatar } from '@brands/services/identity/getUserAvatar';
import { getGenderName } from '@brands/Utils/getGenderName';
import { getTimezoneAbbrWithTimezone } from '@brands/Utils/getParsedTimezoneName';
import ReassignRoleModal from '../Modals/ReassignRoleModal/ReassignRoleModal';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { selectAuth } from '@brands/store/selectors/auth';
import CareAdvisorProfileCardModal from '../../ProfileCards/CareAdvisor/ProfileCards';
import CollectorProfileCardModal from '../../ProfileCards/Collector/ProfileCards';
import ProviderProfileCardModal from '../../ProfileCards/Provider/ProfileCards';

type Props = {
  thisCase: ICase;
  userInfo: UserProfile;
  redirectToConversationPage: (myRoleName: string, withRoleName: string) => void;
  onReassignProvider: (value: string) => Promise<void>;
  onReassignCareAdvisor: (value: string) => Promise<void>;
  onReassignCollector?: (value: string) => Promise<void>;
  setSelectProvidersModal: React.Dispatch<React.SetStateAction<boolean>>;
  onCollectorMessageClick?: (withRole: string) => void;
};

type TeamCardProps = {
  title: string;
  thisCase: ICase;
  user?: any;
  avatarId?: number | null;
  canMessage?: boolean;
  onMessage?: () => void;
  showLocation?: boolean;
  showReassign?: boolean;
  onOpenReassign?: () => void;
  activeOnClosed?: boolean;
};

const TeamCard = ({
  title,
  thisCase,
  user,
  avatarId,
  canMessage,
  onMessage,
  showLocation,
  showReassign,
  onOpenReassign,
  activeOnClosed,
}: TeamCardProps): JSX.Element | null => {
  const { userInfo } = useAppSelector(selectAuth);
  const [caProfileCardModal, setCAProfileCardModal] = useState(false);
  const [collectorProfileCardModal, setCollectorProfileCardModal] = useState(false);
  const [providerProfileCardModal, setProviderProfileCardModal] = useState(false);
  const [profileCardData, setProfileCardData] = useState<any>([]);
  const isCaseClosed = thisCase.status === Status.Completed || thisCase.status === Status.Canceled;
  // A linked OHC/care-coordination case stays "upcoming" until the patient schedules
  // it (video_conference_date set). Reassigning the Care Advisor before that point
  // used to push the case straight to in-progress, which broke the patient's
  // "needs scheduling" routing on My Care Journey, so block reassignment here
  // until the patient has actually scheduled.
  const isUnscheduledLinkedCase =
    (thisCase.type === CaseType.oral_health_coaching || thisCase.type === CaseType.care_coordination) &&
    thisCase.status === Status.Upcoming &&
    !thisCase.video_conference_date;
  const isReassignLocked = (isCaseClosed && !activeOnClosed) || isUnscheduledLinkedCase;
  const isAssignLocked = isCaseClosed;
  if (!user || !avatarId) {
    const canAssign =
      showReassign &&
      onOpenReassign &&
      !isAssignLocked &&
      (userInfo.role.name === UserRoleName.Admin ||
        userInfo.role.name === UserRoleName.SuperAdmin ||
        userInfo.role.name === UserRoleName.OrganizationAdmin ||
        userInfo.role.name === UserRoleName.CareAdvisor);
    if (!canAssign) return null;
    return (
      <div className={styles.consultationPageLeftSideBlockContentFram}>
        <span className={styles.consultationPageLeftSideBlockContentFramCATeamInfoNameTitle}>{title}</span>
        <Button
          variant="outlined"
          className={styles.reassignButton}
          onClick={(e) => {
            e.stopPropagation();
            onOpenReassign();
          }}
        >
          Assign {title}
        </Button>
      </div>
    );
  }

  const openProfileCard = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (user.role.name === UserRoleName.CareAdvisor) {
      setProfileCardData(thisCase.care_advisor);
      setCAProfileCardModal(true);
    } else if (user.role.name === UserRoleName.Provider) {
      setProfileCardData(thisCase.provider);
      setProviderProfileCardModal(true);
    } else if (user.role.name === UserRoleName.Collector) {
      setProfileCardData(thisCase.collector);
      setCollectorProfileCardModal(true);
    }
  };

  return (
    <div className={styles.consultationPageLeftSideBlockContentFram}>
      <div className={styles.consultationPageLeftSideBlockContentFramCATeamInfo}>
        <img
          alt="Avatar"
          className={`fs-exclude ${styles.consultationPageLeftSideBlockContentFramCATeamInfoImage}`}
          src={getUserAvatar(Number(avatarId))}
          role="button"
          tabIndex={0}
          style={{ cursor: 'pointer' }}
          onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
            const img = event.currentTarget;
            img.src = FALLBACK_AVATAR;
            img.onerror = null;
          }}
          onClick={openProfileCard}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openProfileCard(e as unknown as React.MouseEvent);
            }
          }}
        />
        <div className={styles.consultationPageLeftSideBlockContentFramCATeamInfoNameDiv}>
          <div
            className={styles.consultationPageLeftSideBlockContentFramCATeamInfoName}
            role="button"
            tabIndex={0}
            style={{ cursor: 'pointer' }}
            onClick={openProfileCard}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openProfileCard(e as unknown as React.MouseEvent);
              }
            }}
          >
            <span className={styles.consultationPageLeftSideBlockContentFramCATeamInfoNameTitle}>{title}</span>
            <span className={styles.consultationPageLeftSideBlockContentFramCATeamInfoNameValue}>
              {user.full_name} {user.status === 'Deactivated' && '(Deactivated)'}
              {user.provider_profile?.credentials ? ', ' : ''}
              {user.provider_profile?.credentials || ''}
            </span>
          </div>
          {caProfileCardModal && (
            <CareAdvisorProfileCardModal
              isOpen={caProfileCardModal}
              toggleModal={setCAProfileCardModal}
              careAdvisorIdentity={profileCardData}
            />
          )}
          {providerProfileCardModal && (
            <ProviderProfileCardModal
              isOpen={providerProfileCardModal}
              toggleModal={setProviderProfileCardModal}
              providerIdentity={profileCardData}
              patientCurrentStateValue={undefined}
            />
          )}
          {collectorProfileCardModal && (
            <CollectorProfileCardModal
              isOpen={collectorProfileCardModal}
              toggleModal={setCollectorProfileCardModal}
              collectorIdentity={profileCardData}
            />
          )}

          {canMessage && onMessage ? (
            <div className={styles.consultationPageLeftSideBlockContentFramCATeamInfoMessage}>
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  onMessage();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    onMessage();
                  }
                }}
                role="button"
                tabIndex={0}
                style={{ cursor: 'pointer' }}
              >
                <MailOutlineIcon />
              </span>
            </div>
          ) : null}
        </div>
      </div>

      <div className={styles.consultationPageLeftSideBlockContentFramCATeamDetails}>
        <span>
          {getGenderName(user?.basic_info?.gender)}
          <Divider orientation="vertical" flexItem className={styles.divider} />
          {user?.basic_info?.pronouns}
        </span>

        {title !== UserRoleName.Collector && (
          <span>
            Language(s):{' '}
            {user?.basic_info?.languages
              ?.map((language: any) => (typeof language !== 'string' ? language.name : ''))
              ?.join(', ')}
          </span>
        )}

        {showLocation ? (
          <span>
            Location: {user?.address?.state} - {user?.address?.zip_code} - (
            {getTimezoneAbbrWithTimezone(user?.address?.timezone as string)})
          </span>
        ) : null}
      </div>

      {(userInfo.role.name === UserRoleName.Admin ||
        userInfo.role.name === UserRoleName.SuperAdmin ||
        userInfo.role.name === UserRoleName.OrganizationAdmin ||
        (userInfo.role.name === UserRoleName.CareAdvisor &&
          Number(thisCase.care_advisor_id) === Number(userInfo.id) &&
          title === 'Provider') ||
        (userInfo.role.name === UserRoleName.CareAdvisor && title === 'Care Advisor')) &&
        title !== 'Collector' &&
        showReassign &&
        onOpenReassign &&
        !isReassignLocked && (
          <Button
            variant="outlined"
            className={styles.reassignButton}
            onClick={(e) => {
              e.stopPropagation();
              onOpenReassign();
            }}
          >
            Reassign {title}
          </Button>
        )}
    </div>
  );
};

const CareTeamPanel = ({
  thisCase,
  userInfo,
  redirectToConversationPage,
  onReassignProvider,
  onReassignCareAdvisor,
  onReassignCollector,
  setSelectProvidersModal,
  onCollectorMessageClick,
}: Props): JSX.Element => {
  const isCollector = userInfo.role.name === UserRoleName.Collector;
  const [openRole, setOpenRole] = useState<'provider' | 'careAdvisor' | 'collector' | null>(null);
  const [selectedValue, setSelectedValue] = useState('');
  const collectorUser = resolveCollectorUser(thisCase, userInfo);

  const handleOpenReassign = (role: 'provider' | 'careAdvisor' | 'collector', currentId?: number | null): void => {
    if (role !== 'provider') setOpenRole(role);
    else {
      setSelectProvidersModal(true);
    }
  };

  const handleCloseReassign = (): void => {
    setOpenRole(null);
  };

  return (
    <div className={styles.consultationPageLeftSideBlock}>
      <span className={styles.consultationPageLeftSideBlockSpan}>Care team</span>

      <div className={styles.consultationPageLeftSideBlockContent}>
        {/*
          DO-3976: Oral Health Coaching and Care Coordination are
          CA-native case types that don't involve a Provider. Before
          this guard, `TeamCard`'s empty-state branch (line 52-68)
          would show an "Assign Provider" button for CA / Admin /
          Collector roles on these cases, and — when a provider was
          (incorrectly) attached — the full Provider card with a
          "Reassign Provider" button. Skipping the card entirely for
          these two types matches the convention used throughout
          ConsultationPageNewDesign.tsx, where `care_coordination ||
          oral_health_coaching` is the standard "CA-only case type"
          gate. The legacy CareAdvisorLayout.tsx had the same guard
          for care_coordination but never carried it over to
          CareTeamPanel, and OHC was never guarded anywhere.
        */}
        {thisCase.type !== CaseType.oral_health_coaching && thisCase.type !== CaseType.care_coordination && (
          <TeamCard
            title="Provider"
            thisCase={thisCase}
            user={thisCase.provider}
            avatarId={thisCase.provider_id}
            canMessage={isCollector ? true : thisCase.provider_id !== userInfo.id}
            onMessage={
              isCollector
                ? () => onCollectorMessageClick?.(UserRoleName.Provider)
                : () => redirectToConversationPage(userInfo.role.name, UserRoleName.Provider)
            }
            showLocation
            showReassign
            onOpenReassign={() => handleOpenReassign('provider', thisCase.provider_id)}
          />
        )}

        <TeamCard
          title="Care Advisor"
          thisCase={thisCase}
          user={thisCase.care_advisor}
          avatarId={thisCase.care_advisor_id}
          canMessage={isCollector ? true : thisCase.care_advisor_id !== userInfo.id}
          onMessage={
            isCollector
              ? () => onCollectorMessageClick?.(UserRoleName.CareAdvisor)
              : () => redirectToConversationPage(userInfo.role.name, UserRoleName.CareAdvisor)
          }
          showReassign
          onOpenReassign={() => handleOpenReassign('careAdvisor', thisCase.care_advisor_id)}
          activeOnClosed
        />

        {collectorUser && thisCase.collector_id ? (
          <TeamCard
            title="Collector"
            thisCase={thisCase}
            user={collectorUser}
            avatarId={thisCase.collector_id}
            canMessage={thisCase.collector_id !== userInfo.id}
            showReassign
            onOpenReassign={() => handleOpenReassign('collector', thisCase.collector_id)}
          />
        ) : null}
      </div>

      <ReassignRoleModal
        open={Boolean(openRole)}
        role={openRole}
        thisCase={thisCase}
        onClose={() => setOpenRole(null)}
        onApply={async (selectedId) => {
          if (openRole === 'provider') {
            await onReassignProvider(selectedId);
          } else if (openRole === 'careAdvisor') {
            await onReassignCareAdvisor(selectedId);
          }
          setOpenRole(null);
        }}
      />
    </div>
  );
};

export default CareTeamPanel;
